// src/constants/gameMeta.js
// Display-only names/descriptions for bracket previews.
// DOES NOT affect payout bands or Firestore; UI-only.
//
// LEGAL COMPLIANCE NOTES:
// - NO game names in event titles (avoids implying official affiliation)
// - NO "Championship" (trademarked by publishers for official events)
// - NO publisher IP terms (Azeroth, World Series, World Cup, etc.)
// - Use generic esports terminology: Open, Circuit, Series, Cup, Trials,
//   Qualifier, League, Grand Finals, Invitational, Summit, Classic

export const sizeToToken = (n) =>
  (n % 1024 === 0 && n >= 1024 ? `${n/1024}k` : String(n));

/* ---------------- Fighting ---------------- */
export const FIGHTING_BRACKET_META = {
  sf6: {
    '4':   { name: 'Dojo Duel',              description: 'Neighborhood set-play showcase.' },
    '8':   { name: 'Arcade Rumble',          description: 'Quick bracket, fast adjustments.' },
    '16':  { name: 'Metro City Open',        description: 'Classic locals feel—top talent rises.' },
    '32':  { name: 'Challenger Circuit',     description: 'Regional qualifier vibes.' },
    '64':  { name: 'Master Rank Qualifier',  description: 'Deep run—adapt through the gauntlet.' },
    '128': { name: 'Premier Cup',            description: 'Stacked field, storylines everywhere.' },
    '256': { name: 'Proving Grounds',        description: 'Endurance meets matchup knowledge.' },
    '512': { name: 'Global Trials',          description: 'International talent testing ground.' },
    '1k':  { name: 'Continental Cup',        description: 'Best of each region collides.' },
    '2k':  { name: 'Elite Series',           description: 'Only the sharpest survive.' },
    '4k':  { name: 'Grand Master Series',    description: 'Title runs are forged here.' },
    '8k':  { name: 'International Open',     description: 'Worldwide clash of styles.' },
    '16k': { name: 'World Grand Finals',     description: 'One champion, many legends.' },
    '32k': { name: 'Global Legends Invitational', description: 'The largest stage in fighting game history.' },
  },
  tk8: {
    '4':   { name: 'Iron Fist Spar',         description: 'Warm-up brawl to get the reads.' },
    '8':   { name: 'Iron Fist Rumble',       description: 'Short, explosive bracket.' },
    '16':  { name: 'King\'s Arena Open',      description: 'Fundamentals and wall game matter.' },
    '32':  { name: 'Challenger Ring',        description: 'Regional pressure, big momentum swings.' },
    '64':  { name: 'Major Ring Qualifier',   description: 'Punish game must be on point.' },
    '128': { name: 'Steel League',           description: 'Depth of field—endurance required.' },
    '256': { name: 'Grand Iron Cup',         description: 'Every round is a boss fight.' },
    '512': { name: 'Global Iron Trials',     description: 'Styles from everywhere clash.' },
    '1k':  { name: 'Continental Iron',       description: 'Elite regional showdown.' },
    '2k':  { name: 'Master Iron Series',     description: 'Peak execution, zero drops.' },
    '4k':  { name: 'Ultimate Iron Series',   description: 'Legends are made.' },
    '8k':  { name: 'International Iron',     description: 'Worldwide head-to-head.' },
    '16k': { name: 'Iron Grand Finals',      description: 'The final ring.' },
    '32k': { name: 'Iron Fist Legends Invitational', description: 'An unprecedented gathering of warriors.' },
  },
  mk1: {
    '4':   { name: 'Outworld Trials',        description: 'Quick kameo checks and confirms.' },
    '8':   { name: 'Champion\'s Gauntlet',    description: 'Clutch breakers decide sets.' },
    '16':  { name: 'Earthrealm Open',        description: 'Adaptation through varied matchups.' },
    '32':  { name: 'Elder Arena Circuit',    description: 'Spacing and meter mastery matter.' },
    '64':  { name: 'Grand Gauntlet',         description: 'Reads sharpen or you\'re out.' },
    '128': { name: 'Champion\'s Major',       description: 'Stacked from pools to finals.' },
    '256': { name: 'Realm Masters Open',     description: 'Tournament stamina wins here.' },
    '512': { name: 'Global Gauntlet',        description: 'International kameo diversity.' },
    '1k':  { name: 'Continental Champions',  description: 'Regional elite collide.' },
    '2k':  { name: 'Masters Circuit',        description: 'Only killers left.' },
    '4k':  { name: 'Ultimate Gauntlet',      description: 'The road is long.' },
    '8k':  { name: 'World Open',             description: 'Global, unforgiving.' },
    '16k': { name: 'Realm Grand Finals',     description: 'There can be only one.' },
    '32k': { name: 'Kombat Legends Invitational', description: 'The ultimate test of Kombat.' },
  },
  ssb: {
    '4':   { name: 'Platform Club Cup',      description: 'Local friendlies graduate to bracket.' },
    '8':   { name: 'Final Stock Rumble',     description: 'Clutch time every game.' },
    '16':  { name: 'Battlefield Open',       description: 'Neutral mastery gets rewarded.' },
    '32':  { name: 'Challenger Stocks',      description: 'Regional energy, big upsets.' },
    '64':  { name: 'Ultimate Major Qualifier',description: 'Adapt or get edge-guarded.' },
    '128': { name: 'Platform Masters Open',  description: 'Stacked pools to top 8.' },
    '256': { name: 'Platform Premier Cup',   description: 'Depth in every pool.' },
    '512': { name: 'Global Platform Trials', description: 'Styles, counters, secondaries.' },
    '1k':  { name: 'Continental Platform',   description: 'Regional elite assemble.' },
    '2k':  { name: 'Platform Elite Series',  description: 'Execution + nerves of steel.' },
    '4k':  { name: 'Super Platform Open',    description: 'Worldwide scale.' },
    '8k':  { name: 'International Platform', description: 'Only titans remain.' },
    '16k': { name: 'Platform Grand Finals',  description: 'The endgame.' },
    '32k': { name: 'Platform Legends Invitational', description: 'The biggest platform fighter event ever assembled.' },
  },
  // 2XKO - Riot's 2v2 tag team fighter featuring League of Legends champions
  '2xk': {
    '4':   { name: 'Tag Trial',              description: 'Quick duo test—synergy wins.' },
    '8':   { name: 'Duo Rumble',             description: 'Fast bracket, tag timing matters.' },
    '16':  { name: 'Tag Arena Open',         description: 'Team comps and assists shine.' },
    '32':  { name: 'Synergy Circuit',        description: 'Regional pressure, partner chemistry shows.' },
    '64':  { name: 'Tag Team Qualifier',     description: 'Deep run—adapt your duo strat.' },
    '128': { name: 'Tag Masters League',     description: 'Stacked field, diverse tag combos.' },
    '256': { name: 'Proving Grounds',        description: 'Endurance meets team synergy.' },
    '512': { name: 'Global Tag Trials',      description: 'International tag styles collide.' },
    '1k':  { name: 'Continental Cup',        description: 'Best duos from each region.' },
    '2k':  { name: 'Elite Tag Series',       description: 'Only the sharpest pairs survive.' },
    '4k':  { name: 'Grand Tag Series',       description: 'Title runs are forged here.' },
    '8k':  { name: 'International Open',     description: 'Worldwide clash of tag styles.' },
    '16k': { name: 'Tag Grand Finals',       description: 'One duo, legendary status.' },
    '32k': { name: 'Tag Legends Invitational', description: 'The largest stage in tag fighter history.' },
  },
};

export function getFightingBracketMeta(gameKey, bracketSize) {
  const meta = FIGHTING_BRACKET_META?.[gameKey]?.[sizeToToken(bracketSize)];
  return meta || null;
}

/* ---------------- MOBA ---------------- */
/**
 * Keys should match GAME_PROFILES for MOBAs, e.g.:
 * - lol  (League of Legends)
 * - dt2  (Dota 2)
 * - sm2  (SMITE 2)
 * Rename keys to match your actual gameData profile keys if needed.
 */
export const MOBA_BRACKET_META = {
  lol: {
    '4':   { name: 'Summoner Skirmish',        description: 'Tight 4-team skirmish on the Rift.' },
    '8':   { name: 'Rift Rumble',              description: 'Short bracket—drafts must land.' },
    '16':  { name: 'Nexus Open',               description: 'Classic open—macro wins days.' },
    '32':  { name: 'Regional Rift Circuit',    description: 'Regionals energy—upsets brew.' },
    '64':  { name: 'Baron Qualifier',          description: 'Depth tests shotcalling stamina.' },
    '128': { name: 'Challenger League',        description: 'Every series is a finals rematch.' },
    '256': { name: 'Grand Rift Cup',           description: 'Endurance + crisp resets.' },
    '512': { name: 'Global Rift Trials',       description: 'International strats collide.' },
    '1k':  { name: 'Continental Rift Series',  description: 'Top seeds meet dark horses.' },
    '2k':  { name: 'Elite Rift Series',        description: 'Draft mastery across the field.' },
    '4k':  { name: 'Grand Rift Series',        description: 'Macro kings and upset artists.' },
    '8k':  { name: 'International Rift Open',  description: 'Worldwide strategies clash.' },
    '16k': { name: 'Rift Grand Finals',        description: 'Legends are crowned.' },
    '32k': { name: 'Rift Legends Invitational', description: 'The ultimate convergence of Summoners.' },
  },
  dt2: {
    '4':   { name: 'Ancient Skirmish',         description: 'Shadow picks and timing fights.' },
    '8':   { name: 'Radiant vs Dire Rumble',   description: 'Explosive best-of swings.' },
    '16':  { name: 'Ancient Open',             description: 'Vision & smoke discipline matter.' },
    '32':  { name: 'Regional Battle Cup',      description: 'Draft flexes win series.' },
    '64':  { name: 'Roshan Qualifier',         description: 'Objective trades under pressure.' },
    '128': { name: 'Legends League',           description: 'Greed vs tempo—choose wisely.' },
    '256': { name: 'Grand Ancient Cup',        description: 'Attrition and clutch saves.' },
    '512': { name: 'Global Ancient Trials',    description: 'Meta breakers vs purists.' },
    '1k':  { name: 'Continental Ancient',      description: 'Regional elite collide.' },
    '2k':  { name: 'Elite Ancient Series',     description: 'Sharp drafts, tighter windows.' },
    '4k':  { name: 'Grand Ancient Series',     description: 'Marathon of mind games.' },
    '8k':  { name: 'International Ancient',    description: 'Global meta on display.' },
    '16k': { name: 'Ancient Grand Finals',     description: 'The Aegis of glory.' },
    '32k': { name: 'Ancient Legends Invitational', description: 'Where Dota immortality is earned.' },
  },
  sm2: {
    '4':   { name: 'Pantheon Skirmish',        description: 'Gods clash in a quick bracket.' },
    '8':   { name: 'Battleground Brawl',       description: 'Early fights, snowball potential.' },
    '16':  { name: 'Elysium Open',             description: 'Rotations and relics decide it.' },
    '32':  { name: 'Regional Pantheon',        description: 'Objective control under stress.' },
    '64':  { name: 'Fire Giant Qualifier',     description: 'Shot calls in the fire pit.' },
    '128': { name: 'Champion\'s Pantheon',      description: 'Draft mind-games throughout.' },
    '256': { name: 'Grand Battleground Cup',   description: 'Endurance + counterpicks.' },
    '512': { name: 'Global Battleground',      description: 'Clash of styles worldwide.' },
    '1k':  { name: 'Continental Battleground', description: 'Kings are crowned.' },
    '2k':  { name: 'Elite Battleground',       description: 'Late-game discipline matters.' },
    '4k':  { name: 'Grand Battleground',       description: 'Macro mastery over hours.' },
    '8k':  { name: 'International Battleground',description: 'World-class calls and combos.' },
    '16k': { name: 'Battleground Grand Finals', description: 'Immortals are made.' },
    '32k': { name: 'Divine Legends Invitational', description: 'Gods among mortals.' },
  },
};

export function getMobaBracketMeta(gameKey, bracketSize) {
  const meta = MOBA_BRACKET_META?.[gameKey]?.[sizeToToken(bracketSize)];
  return meta || null;
}

export const RTS_BRACKET_META = {
  // StarCraft II
  sc2: {
    '4':   { name: 'Sector Skirmish',         description: 'Micro duels and crisp builds decide it.' },
    '8':   { name: 'Warp Gate Rumble',        description: 'Open bracket with sharp timings.' },
    '16':  { name: 'Koprulu Open',            description: 'Macro vs. aggression across the field.' },
    '32':  { name: 'Regional Sector Circuit', description: 'Qualifiers energy—hold the line.' },
    '64':  { name: 'Colossus Qualifier',      description: 'Stamina in series, map prep matters.' },
    '128': { name: 'Ascendant League',        description: 'Every round a new read to make.' },
    '256': { name: 'Grand Koprulu Cup',       description: 'Late-game chops and multitask checks.' },
    '512': { name: 'Global Sector Trials',    description: 'International styles collide.' },
    '1k':  { name: 'Continental Sector',      description: 'Top seeds meet dark horses.' },
    '2k':  { name: 'Elite Sector Series',     description: 'Perfect scouting and punishes.' },
    '4k':  { name: 'Grand Sector Series',     description: 'Endurance through epic series.' },
    '8k':  { name: 'International Sector',    description: 'Meta breakers vs. purists.' },
    '16k': { name: 'Sector Grand Finals',     description: 'One banner at the end.' },
  },
  // Age of Empires II
  ae2: {
    '4':   { name: 'Feudal Skirmish',         description: 'Dark→Feudal all-ins or fast castle?' },
    '8':   { name: 'Castle Clash',            description: 'Macro, map control, and scouts.' },
    '16':  { name: 'Empire Open',             description: 'Civ prep meets on-the-fly reads.' },
    '32':  { name: 'Regional Empire Circuit', description: 'Boom timings and pressure windows.' },
    '64':  { name: 'Imperial Qualifier',      description: 'Trebs out—hold your ground.' },
    '128': { name: 'Warlord League',          description: 'Eco discipline across long sets.' },
    '256': { name: 'Grand Empire Cup',        description: 'Multi-map endurance and counters.' },
    '512': { name: 'Global Empire Trials',    description: 'Civ pools pushed to the limit.' },
    '1k':  { name: 'Continental Empire',      description: 'Best prep meets best adaptation.' },
    '2k':  { name: 'Elite Empire Series',     description: 'Relics, trade, and timing fights.' },
    '4k':  { name: 'Grand Empire Series',     description: 'Late-game mastery across maps.' },
    '8k':  { name: 'International Empire',    description: 'Worlds collide on Arabia and more.' },
    '16k': { name: 'Empire Grand Finals',     description: 'Crown the conqueror.' },
  },
  // Warcraft III
  wc3: {
    '4':   { name: 'Grunt Skirmish',          description: 'Harass routes and micro clinics.' },
    '8':   { name: 'Hero Duel Rumble',        description: 'Creep paths, timings, surrounds.' },
    '16':  { name: 'Warfront Open',           description: 'Heroes, items, and army comp checks.' },
    '32':  { name: 'Regional Warfront Circuit',description: 'Tech paths meet tempo pushes.' },
    '64':  { name: 'Moonwell Qualifier',      description: 'Creep jacks decide destinies.' },
    '128': { name: 'Legends League',          description: 'Nerves of steel in base races.' },
    '256': { name: 'Grand Warfront Cup',      description: 'Endurance through map pool variety.' },
    '512': { name: 'Global Warfront Trials',  description: 'World-class micro theater.' },
    '1k':  { name: 'Continental Warfront',    description: 'Best-in-region runbacks.' },
    '2k':  { name: 'Elite Warfront Series',   description: 'Tier-3 tech and split-map wars.' },
    '4k':  { name: 'Grand Warfront Series',   description: 'Hero itemization masterclass.' },
    '8k':  { name: 'International Warfront',  description: 'Clash of eras and styles.' },
    '16k': { name: 'Warfront Grand Finals',   description: 'The throne awaits.' },
  },
  // Total War: Warhammer 3
  tw3: {
    '4':   { name: 'Realm Skirmish',          description: 'Spellcraft and cycle charges.' },
    '8':   { name: 'Legion Rumble',           description: 'Buildcraft and target priority.' },
    '16':  { name: 'Old World Open',          description: 'Micro and morale management.' },
    '32':  { name: 'Regional Realm Circuit',  description: 'Counters into faction picks.' },
    '64':  { name: 'Everborn Qualifier',      description: 'Artillery lines vs. skirmish kites.' },
    '128': { name: 'High Council League',     description: 'Army comps stress-tested.' },
    '256': { name: 'Grand Realm Cup',         description: 'Attrition and map control matter.' },
    '512': { name: 'Global Realm Trials',     description: 'Styles from every province.' },
    '1k':  { name: 'Continental Realm',       description: 'Only the steadiest command.' },
    '2k':  { name: 'Elite Realm Series',      description: 'Flanks, fear, and focus fire.' },
    '4k':  { name: 'Grand Realm Series',      description: 'Endurance across sieges and fields.' },
    '8k':  { name: 'International Realm',     description: 'Epic scale, elite generals.' },
    '16k': { name: 'Realm Grand Finals',      description: 'The final clash of empires.' },
  },
};

export function getRtsBracketMeta(gameKey, bracketSize) {
  const meta = RTS_BRACKET_META?.[gameKey]?.[sizeToToken(bracketSize)];
  return meta || null;
}

/* ---------------- SHOOTER ---------------- */
// Assumes sizeToToken(n) is already defined above.

export const SHOOTER_BRACKET_META = {
  // Rainbow Six Siege
  rb6: {
    '4':   { name: 'Breach & Clear Skirmish',   description: 'Tight read on strats and trades.' },
    '8':   { name: 'Site Take Rumble',          description: 'Short bracket—executes must hit.' },
    '16':  { name: 'Siege Open',                description: 'Attack/Defend discipline decides it.' },
    '32':  { name: 'Regional Siege Circuit',    description: 'Qualifiers energy—counter-strats matter.' },
    '64':  { name: 'Diamond Qualifier',         description: 'Utility, timing, and nerves.' },
    '128': { name: 'Elite Operator League',     description: 'Stacks on stacks of IGL reads.' },
    '256': { name: 'Grand Siege Cup',           description: 'Endurance through map pool variety.' },
    '512': { name: 'Global Siege Trials',       description: 'Styles and defaults worldwide.' },
    '1k':  { name: 'Continental Siege',         description: 'Top seeds vs. dark horses.' },
    '2k':  { name: 'Elite Siege Series',        description: 'Only perfect executes survive.' },
    '4k':  { name: 'Grand Siege Series',        description: 'Marathon of counter-utility.' },
    '8k':  { name: 'International Siege',       description: 'Global meta on display.' },
    '16k': { name: 'Siege Grand Finals',        description: 'One roster lifts the trophy.' },
  },

  // Call of Duty
  cod: {
    '4':   { name: 'S&D Skirmish',              description: 'First bloods and retakes define sets.' },
    '8':   { name: 'Operator Rumble',           description: 'Tempo swings and clutch moments.' },
    '16':  { name: 'Callsign Open',             description: 'Fundamentals across modes.' },
    '32':  { name: 'Regional Ops Circuit',      description: 'Rotations and map control matter.' },
    '64':  { name: 'Prestige Qualifier',        description: 'Reps under pressure.' },
    '128': { name: 'Veteran League',            description: 'Comms and crossfires—no freebies.' },
    '256': { name: 'Grand Ops Cup',             description: 'Momentum through long runs.' },
    '512': { name: 'Global Ops Trials',         description: 'International playbook clash.' },
    '1k':  { name: 'Continental Ops',           description: 'Seeds collide across regions.' },
    '2k':  { name: 'Elite Ops Series',          description: 'Micro advantages snowball.' },
    '4k':  { name: 'Grand Ops Series',          description: 'Stamina + adaptability.' },
    '8k':  { name: 'International Ops',         description: 'Meta settles—upsets rise.' },
    '16k': { name: 'Ops Grand Finals',          description: 'Only one banner remains.' },
    '32k': { name: 'Ops Legends Invitational',  description: 'The largest tactical showdown ever.' },
  },

  // Marvel Rivals
  mvr: {
    '4':   { name: 'Convergence Skirmish',      description: 'Quick 6v6 reads around objectives.' },
    '8':   { name: 'Multiverse Rumble',         description: 'Hero combos and ult economy.' },
    '16':  { name: 'Rivals Open',               description: 'Macro rotations decide fights.' },
    '32':  { name: 'Regional Convergence Circuit', description: 'Map picks and counter-lines.' },
    '64':  { name: 'Omega Qualifier',           description: 'Teamfight discipline under fire.' },
    '128': { name: 'Alpha League',              description: 'Cooldown trading at scale.' },
    '256': { name: 'Grand Rivals Cup',          description: 'Endurance across modes.' },
    '512': { name: 'Global Rivals Trials',      description: 'Worldwide hero pools collide.' },
    '1k':  { name: 'Continental Convergence',   description: 'Top seeds tested early.' },
    '2k':  { name: 'Elite Convergence Series',  description: 'Only crisp engages survive.' },
    '4k':  { name: 'Grand Convergence Series',  description: 'Marathon of macro reads.' },
    '8k':  { name: 'International Convergence', description: 'Meta breakers vs. purists.' },
    '16k': { name: 'Convergence Grand Finals',  description: 'Final arc resolved.' },
    '32k': { name: 'Multiverse Legends Invitational', description: 'Assemble the greatest heroes.' },
  },

  // Valorant
  val: {
    '4':   { name: 'Spike Skirmish',            description: 'Execs and post-plants, distilled.' },
    '8':   { name: 'Site Exec Rumble',          description: 'Short bracket—utility value counts.' },
    '16':  { name: 'Protocol Open',             description: 'Fundamentals across the map pool.' },
    '32':  { name: 'Regional Spike Circuit',    description: 'Clutch factor meets prep.' },
    '64':  { name: 'Radiant Qualifier',         description: 'Reads and re-clears under pressure.' },
    '128': { name: 'Protocol League',           description: 'Adapting defaults round to round.' },
    '256': { name: 'Grand Spike Cup',           description: 'Economic control across runs.' },
    '512': { name: 'Global Spike Trials',       description: 'International comps collide.' },
    '1k':  { name: 'Continental Protocol',      description: 'Seeds and superteams converge.' },
    '2k':  { name: 'Elite Protocol Series',     description: 'Perfect spacing, perfect trades.' },
    '4k':  { name: 'Grand Protocol Series',     description: 'Depth wins over time.' },
    '8k':  { name: 'International Protocol',    description: 'World-class IGLs shine.' },
    '16k': { name: 'Protocol Grand Finals',     description: 'Legends are minted.' },
    '32k': { name: 'Protocol Legends Invitational', description: 'Protocol: Achieve immortality.' },
  },

  // Counter-Strike 2
  cs2: {
    '4':   { name: 'Pistol Round Skirmish',     description: 'Aim duels and protocols, distilled.' },
    '8':   { name: 'Site Take Rumble',          description: 'Utility value and spacing matter.' },
    '16':  { name: 'Defuse Open',               description: 'Mid-rounding wins series.' },
    '32':  { name: 'Regional Bombsite Circuit', description: 'Execs vs. anchors—who bends?' },
    '64':  { name: 'Premier Qualifier',         description: 'Multi-map resilience checked.' },
    '128': { name: 'Pro League',                description: 'No freebies—protocols on point.' },
    '256': { name: 'Grand Counter Cup',         description: 'Clutches and resets over time.' },
    '512': { name: 'Global Counter Trials',     description: 'International defaults collide.' },
    '1k':  { name: 'Continental Counter',       description: 'Top seeds meet form teams.' },
    '2k':  { name: 'Elite Counter Series',      description: 'Only perfect trading survives.' },
    '4k':  { name: 'Grand Counter Series',      description: 'Deep map pools rewarded.' },
    '8k':  { name: 'International Counter',     description: 'World meta crystallizes.' },
    '16k': { name: 'Counter Grand Finals',      description: 'One dynasty stands tall.' },
    '32k': { name: 'Counter Legends Invitational', description: 'The biggest Major in CS history.' },
  },

  // Apex Legends
  apl: {
    '4':   { name: 'Drop Zone Skirmish',        description: 'Fights on POIs and quick rotates.' },
    '8':   { name: 'Ring Rumble',               description: 'Zone reads and third-party control.' },
    '16':  { name: 'Outlands Open',             description: 'Macro rotates decide endgames.' },
    '32':  { name: 'Regional Outlands Circuit', description: 'Edge vs. zone, composition tests.' },
    '64':  { name: 'Predator Qualifier',        description: 'Finals nerves in the ring.' },
    '128': { name: 'Apex League',               description: 'KP vs. placement balance.' },
    '256': { name: 'Grand Outlands Cup',        description: 'Endurance across map swaps.' },
    '512': { name: 'Global Outlands Trials',    description: 'Worldwide IGL philosophies.' },
    '1k':  { name: 'Continental Outlands',      description: 'Regional titans collide.' },
    '2k':  { name: 'Elite Outlands Series',     description: 'Only clean comms survive.' },
    '4k':  { name: 'Grand Outlands Series',     description: 'Late-game discipline rules.' },
    '8k':  { name: 'International Outlands',    description: 'Meta lines converge.' },
    '16k': { name: 'Outlands Grand Finals',     description: 'Legends are crowned.' },
    '32k': { name: 'Outlands Legends Invitational', description: 'The ultimate proving ground.' },
  },

  // Overwatch 2
  ow2: {
    '4':   { name: 'Payload Skirmish',          description: 'Ult economy and target focus.' },
    '8':   { name: 'Control Point Rumble',      description: 'Win conditions around spawns.' },
    '16':  { name: 'Hero Open',                 description: 'Map pool discipline wins days.' },
    '32':  { name: 'Regional Hero Circuit',     description: 'Mirror comps and counters.' },
    '64':  { name: 'Grandmaster Qualifier',     description: 'Cooldown trades at scale.' },
    '128': { name: 'Hero League',               description: 'Adaptation across roles.' },
    '256': { name: 'Grand Hero Cup',            description: 'Endurance through push/escort/control.' },
    '512': { name: 'Global Hero Trials',        description: 'Worldwide comps collide.' },
    '1k':  { name: 'Continental Hero',          description: 'Top seeds stress-tested.' },
    '2k':  { name: 'Elite Hero Series',         description: 'Only pristine fights advance.' },
    '4k':  { name: 'Grand Hero Series',         description: 'Depth across long series.' },
    '8k':  { name: 'International Hero',        description: 'Meta stabilizes—upsets rise.' },
    '16k': { name: 'Hero Grand Finals',         description: 'One roster, one crown.' },
    '32k': { name: 'Hero Legends Invitational', description: 'Heroes never die—legends never fade.' },
  },
};

export function getShooterBracketMeta(gameKey, bracketSize) {
  const meta = SHOOTER_BRACKET_META?.[gameKey]?.[sizeToToken(bracketSize)];
  return meta || null;
}

/* ---------------- SPORTS ---------------- */
// Keys must match GAME_PROFILES: mdn, efc, n2k, mbs, rkl
export const SPORTS_BRACKET_META = {
  // Madden
  mdn: {
    '4':   { name: 'Red Zone Skirmish',        description: 'Two-minute drill energy—every snap counts.' },
    '8':   { name: 'Gridiron Rumble',          description: 'Short bracket—clock and timeouts matter.' },
    '16':  { name: 'Playoff Open',             description: 'Bracket runs start with clean reads.' },
    '32':  { name: 'Conference Circuit',       description: 'Mix your playbook, punish habits.' },
    '64':  { name: 'Playoff Qualifier',        description: 'Ball control vs. big plays—pick a lane.' },
    '128': { name: 'Hail Mary Series',         description: 'Pray for a chance to win it all.' },
    '256': { name: 'Grand Gridiron Cup',       description: 'Fourth-quarter nerves decide legends.' },
    '512': { name: 'Global Gridiron Trials',   description: 'Schemes collide from every region.' },
    '1k':  { name: 'Continental Gridiron',     description: 'Top seeds vs. fearless underdogs.' },
    '2k':  { name: 'Elite Gridiron Series',    description: 'Perfect reads, perfect clocks.' },
    '4k':  { name: 'Grand Gridiron Series',    description: 'Sustained excellence over rounds.' },
    '8k':  { name: 'International Gridiron',   description: 'World playbooks on display.' },
    '16k': { name: 'Gridiron Grand Finals',    description: 'Hoist the digital Lombardi.' },
    '32k': { name: 'Gridiron Legends Classic', description: 'The biggest stage in virtual football.' },
  },

  // EA Sports FC
  efc: {
    '4':   { name: 'Pitch Skirmish',           description: 'Tight fixtures—first goal swings it.' },
    '8':   { name: 'Derby Rumble',             description: 'Pressing and counters in a sprint.' },
    '16':  { name: 'Club Open',                description: 'Composure in the box wins ties.' },
    '32':  { name: 'Regional Pitch Circuit',   description: 'Build-up vs. quick breaks—choose.' },
    '64':  { name: 'Champions Qualifier',      description: 'Clinical finishing under pressure.' },
    '128': { name: 'Elite Club League',        description: 'Formations flex, meta evolves.' },
    '256': { name: 'Grand Pitch Cup',          description: 'Stamina across legs and ties.' },
    '512': { name: 'Global Pitch Trials',      description: 'Styles from every region collide.' },
    '1k':  { name: 'Continental Club',         description: 'Seeds and dark horses meet.' },
    '2k':  { name: 'Elite Club Series',        description: 'Margins measured in millimeters.' },
    '4k':  { name: 'Grand Club Series',        description: 'Ice-cold in penalty moments.' },
    '8k':  { name: 'International Club',       description: 'World football philosophies clash.' },
    '16k': { name: 'Club Grand Finals',        description: 'Lift the ultimate silverware.' },
    '32k': { name: 'Pitch Legends Classic',    description: 'Football immortality awaits.' },
  },

  // NBA 2K
  n2k: {
    '4':   { name: 'Court Skirmish',           description: 'Shot timing and lanes decide it.' },
    '8':   { name: 'Blacktop Rumble',          description: 'Momentum runs—don\'t give it back.' },
    '16':  { name: 'Court Open',               description: 'Pick-and-roll chess across rounds.' },
    '32':  { name: 'Regional Court Circuit',   description: 'Stamina and spacing win sets.' },
    '64':  { name: 'All-Star Qualifier',       description: 'Hot zones + green windows matter.' },
    '128': { name: 'Pro-Am League',            description: 'Perfect rotations, no freebies.' },
    '256': { name: 'Grand Hoops Cup',          description: 'Clutch badges shine late.' },
    '512': { name: 'Global Hoops Trials',      description: 'Meta builds vs. pure skill.' },
    '1k':  { name: 'Continental Hoops',        description: 'Legends write new chapters.' },
    '2k':  { name: 'Elite Hoops Series',       description: 'Microscopic timing margins.' },
    '4k':  { name: 'Grand Hoops Series',       description: 'Composure through game 7s.' },
    '8k':  { name: 'International Hoops',      description: 'Global styles on hardwood.' },
    '16k': { name: 'Hoops Grand Finals',       description: 'Rings, banners, history.' },
    '32k': { name: 'Hoops Legends Classic',    description: 'The dynasty-defining tournament.' },
  },

  // MLB The Show
  mbs: {
    '4':   { name: 'Sandlot Skirmish',         description: 'Pitch mix vs. plate discipline.' },
    '8':   { name: 'Diamond Rumble',           description: 'Bullpen management wins series.' },
    '16':  { name: 'Ballpark Open',            description: 'Small ball or moonshots—your call.' },
    '32':  { name: 'Regional Pennant Circuit', description: 'Fielding gems swing momentum.' },
    '64':  { name: 'Pennant Qualifier',        description: 'Clutch two-out knocks seal it.' },
    '128': { name: 'Pro Diamond League',       description: 'Rotation depth gets tested.' },
    '256': { name: 'Grand Diamond Cup',        description: 'Late-inning nerves of steel.' },
    '512': { name: 'Global Diamond Trials',    description: 'Different parks, same pressure.' },
    '1k':  { name: 'Continental Diamond',      description: 'Ace vs. ace—who blinks first?' },
    '2k':  { name: 'Elite Diamond Series',     description: 'PCI precision over marathons.' },
    '4k':  { name: 'Grand Diamond Series',     description: 'String hits, strand runners.' },
    '8k':  { name: 'International Diamond',    description: 'Pitch labs vs. pure reads.' },
    '16k': { name: 'Diamond Grand Finals',     description: 'Dogpile on the mound time.' },
    '32k': { name: 'Diamond Legends Classic',  description: 'October glory on the grandest scale.' },
  },

  // Rocket League
  rkl: {
    '4':   { name: 'Kickoff Skirmish',         description: 'Boost paths and 50s decide it.' },
    '8':   { name: 'Aerial Rumble',            description: 'Speed, touches, and demos.' },
    '16':  { name: 'Arena Open',               description: 'Rotations tight, shots tighter.' },
    '32':  { name: 'Regional Supersonic Circuit', description: 'Boost economy under fire.' },
    '64':  { name: 'Champions Qualifier',      description: 'In-field passes vs. solo plays.' },
    '128': { name: 'Grand Champ League',       description: 'Consistency across series.' },
    '256': { name: 'Grand Rocket Cup',         description: 'OT ice in every game.' },
    '512': { name: 'Global Rocket Trials',     description: 'Meta shifts meet adaptability.' },
    '1k':  { name: 'Continental Rocket',       description: 'Flair meets fundamentals.' },
    '2k':  { name: 'Elite Rocket Series',      description: 'Tiny touches, huge impact.' },
    '4k':  { name: 'Grand Rocket Series',      description: 'Ceiling plays to in-n-outs.' },
    '8k':  { name: 'International Rocket',     description: 'Different regions, same pop-offs.' },
    '16k': { name: 'Supersonic Grand Finals',  description: 'One lift to the ceiling.' },
    '32k': { name: 'Supersonic Legends Classic', description: 'Supersonic legends take flight.' },
  },
};

export function getSportsBracketMeta(gameKey, bracketSize) {
  const meta = SPORTS_BRACKET_META?.[gameKey]?.[sizeToToken(bracketSize)];
  return meta || null;
}